import CapegoIntegration from "./CapegoIntegration.js";
import EconomicIntegration from "./EconomicIntegration.js";
import FortnoxExtendedIntegration from "./FortnoxExtendedIntegration.js";
import FortnoxIntegration from "./FortnoxIntegration.js";
import ManualImportIntegration from "./ManualImportIntegration.js";
import ModelFactory from "../ModelFactory.js";
import OiIntegration from "./OiIntegration.js";
import PowerOfficeGoIntegration from "./PowerOfficeGoIntegration.js";
import QuickbooksIntegration from "./QuickbooksIntegration.js";
import SaftIntegration from "./SaftIntegration.js";
import SieImportIntegration from "./SieImportIntegration.js";
import TfsoIntegration from "./TfsoIntegration.js";
import TripletexIntegration from "./TripletexIntegration.js";
import TwinfieldIsaasIntegration from "./TwinfieldIsaasIntegration.js";
import UnicontaIsaasIntegration from "./UnicontaIsaasIntegration.js";
import XeroIntegration from "./XeroIntegration.js";
import XledgerIntegration from "./XledgerIntegration.js";

/**
 * `IntegrationFactory` instantiates the correct integration class depending on the
 * integration type.
 */
class IntegrationFactory extends ModelFactory {
	constructor() {
		super(...arguments);

		this._classesDictionary = {
			SieImportIntegration,
			EconomicIntegration,
			FortnoxIntegration,
			FortnoxExtendedIntegration,
			PowerOfficeGoIntegration,
			TripletexIntegration,
			XeroIntegration,
			TfsoIntegration,
			SaftIntegration,
			OiIntegration,
			XledgerIntegration,
			QuickbooksIntegration,
			ManualImportIntegration,
			TwinfieldIsaasIntegration,
			UnicontaIsaasIntegration,
			CapegoIntegration,
		};
	}

	allVisibleIntegrationClasses() {
		return this.allClasses().filter((Integration) => {
			const instance = new Integration();

			return instance.importerIsVisible();
		});
	}

	allIntegrations() {
		return this.allClasses().map((Class) => new Class());
	}

	allVisibleIntegrations() {
		return this.allIntegrations().filter((integration) =>
			integration.importerIsVisible(),
		);
	}

	allClasses() {
		return Object.values(this._classesDictionary);
	}

	newIntegrationClasses() {
		return [
			SieImportIntegration,
			FortnoxIntegration,
			FortnoxExtendedIntegration,
			EconomicIntegration,
			TripletexIntegration,
			SaftIntegration,
			OiIntegration,
			TfsoIntegration,
			XledgerIntegration,
			XeroIntegration,
			QuickbooksIntegration,
			PowerOfficeGoIntegration,
			TwinfieldIsaasIntegration,
			UnicontaIsaasIntegration,
			CapegoIntegration,
		];
	}

	integrationsUsingNewWizard() {
		return this.newIntegrationClasses().map((Class) => new Class());
	}

	integrationsWithVisibleImporterAndUsingNewWizard() {
		return this.integrationsUsingNewWizard().filter(
			(integration) =>
				integration.importerIsVisible() &&
				integration.importerCanCreateNewCompany(),
		);
	}

	integrationsWithCreatingImporter() {
		return this.allVisibleIntegrations().filter((integration) =>
			integration.importerCanCreateNewCompany(),
		);
	}

	integrationsWithUpdatingImporters() {
		return this.allVisibleIntegrations().filter((integration) =>
			integration.importerCanUpdateExistingCompany(),
		);
	}

	addApplicationActionForEveryIntegration(application, importerDocument) {
		this.integrationsUsingNewWizard().forEach((integration) => {
			const action = this._applicationActionForIntegration(
				integration,
				importerDocument,
			);

			application.addAction(action);
		});
	}

	classFromName(className) {
		return this._classesDictionary[className];
	}

	newIntegration(integrationName) {
		const Class = this.classFromName(integrationName);
		return new Class();
	}

	newIntegrations(integrationNames) {
		return integrationNames.map((name) => this.newIntegration(name));
	}

	_applicationActionForIntegration(integration, importerDocument) {
		return {
			name: integration.importPath(),
			pattern: `import/${integration.routeName()}`,
			cachedDoc: true,
			doc: importerDocument({
				integrationType: integration.getType(),
				name: integration.getName(),
			}),
			action: (doc, query) => {
				doc.show({
					isExistingLightCompany: query.isExistingLightCompany,
					businessIdentityCode: query.businessIdentityCode,
					returnUrl: query.returnUrl,
					abortUrl: query.abortUrl,
				});
			},
		};
	}
}

const integrationFactory = new IntegrationFactory();

export default integrationFactory;
